import { useCallback, useState } from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { RefreshCw, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { OpsShell } from "@/components/admin/OpsShell";
import { axis, Btn, Card, chartTip, Empty, gridStroke, KPI, Loading, T, usePoll, useOpsTheme } from "@/components/admin/ui";

const WINDOWS = [
  { h: 1, label: "1h" },
  { h: 6, label: "6h" },
  { h: 24, label: "24h" },
  { h: 72, label: "3d" },
  { h: 168, label: "7d" },
];

export default function AdminMonitoring() {
  const { dark } = useOpsTheme();
  const [hours, setHours] = useState(24);
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/api-monitoring?hours=${hours}`);
      setD(data);
    } finally {
      setBusy(false);
    }
  }, [hours]);
  usePoll(load, 20000, [hours]);

  return (
    <OpsShell
      title="API monitor"
      subtitle={d ? `Aggregated minute buckets · ${d.retention_days}-day retention · no raw request logs stored` : "Aggregated counters"}
      actions={
        <div className="flex items-center gap-1.5">
          <div className="flex rounded-md border border-slate-300 dark:border-slate-700 overflow-hidden">
            {WINDOWS.map(({ h, label }) => (
              <button
                key={h}
                data-testid={`monitor-window-${h}`}
                onClick={() => setHours(h)}
                className={`px-2.5 py-1.5 text-[12px] font-medium transition-colors ${
                  hours === h
                    ? "bg-emerald-600 text-white dark:bg-emerald-500 dark:text-slate-950"
                    : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <Btn data-testid="admin-refresh-monitoring" onClick={load} className="px-2">
            <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
          </Btn>
        </div>
      }
    >
      {!d ? (
        <Loading label="Reading metric buckets" />
      ) : (
        <div className="space-y-5" data-testid="admin-monitoring">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <KPI testid="mon-total" label="Requests" value={d.total_requests} footer={`last ${d.window_hours}h`} />
            <KPI testid="mon-latency" label="Avg latency" value={`${d.avg_response_ms}ms`} />
            <KPI testid="mon-4xx" label="4xx client errors" value={d.errors_4xx} tone={d.errors_4xx ? "slate" : "emerald"} />
            <KPI testid="mon-5xx" label="5xx server errors" value={d.errors_5xx} tone={d.errors_5xx ? "rose" : "emerald"} />
            <KPI testid="mon-peak" label="Peak per minute" value={d.peak_per_minute} />
          </div>

          <Card testid="mon-timeline" title="Traffic" hint="Requests per minute, last 60 buckets">
            <div className="h-[200px] p-4 pt-5">
              {d.timeline.length === 0 ? (
                <Empty label="No traffic in this window" />
              ) : (
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                  <AreaChart data={d.timeline} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gT" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke={gridStroke(dark)} vertical={false} />
                    <XAxis dataKey="minute" {...axis(dark)} interval="preserveStartEnd" />
                    <YAxis {...axis(dark)} width={42} />
                    <Tooltip {...chartTip(dark)} />
                    <Area type="monotone" name="Requests" dataKey="count" stroke="#10b981" strokeWidth={2} fill="url(#gT)" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </Card>

          <div className="grid xl:grid-cols-2 gap-4">
            <Card testid="mon-top-endpoints" title="Top endpoints" hint="Busiest routes in this window">
              <div className="max-h-[360px] overflow-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className={T.th}>Endpoint</th>
                      <th className={T.th}>Hits</th>
                      <th className={T.th}>Avg</th>
                      <th className={T.th}>Max</th>
                      <th className={T.th}>Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.top_endpoints.map((e, i) => (
                      <tr key={`${e.endpoint}-${i}`} className={T.row}>
                        <td className={`${T.td} ${T.num} text-xs`}>{e.endpoint}</td>
                        <td className={`${T.td} ${T.num}`}>{e.count}</td>
                        <td className={`${T.td} ${T.num}`}>{e.avg_ms}ms</td>
                        <td className={`${T.td} ${T.num} ${T.muted}`}>{e.max_ms}ms</td>
                        <td className={`${T.td} ${T.num} ${e.errors ? "text-rose-600 dark:text-rose-400" : T.muted}`}>{e.errors}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {d.top_endpoints.length === 0 && <Empty label="No endpoint data" />}
              </div>
            </Card>

            <Card testid="mon-failing" title="Failing endpoints" hint="Ordered by error count">
              <div className="max-h-[360px] overflow-auto">
                {d.failing_endpoints.length === 0 ? (
                  <Empty label="No errors — all clear" hint="Nothing has failed in this window" icon={ShieldCheck} />
                ) : (
                  <table className="w-full border-collapse">
                    <thead>
                      <tr>
                        <th className={T.th}>Endpoint</th>
                        <th className={T.th}>4xx</th>
                        <th className={T.th}>5xx</th>
                        <th className={T.th}>Hits</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.failing_endpoints.map((e, i) => (
                        <tr key={`${e.endpoint}-${i}`} className={T.row}>
                          <td className={`${T.td} ${T.num} text-xs`}>{e.endpoint}</td>
                          <td className={`${T.td} ${T.num} text-amber-600 dark:text-amber-400`}>{e.s4}</td>
                          <td className={`${T.td} ${T.num} text-rose-600 dark:text-rose-400`}>{e.s5}</td>
                          <td className={`${T.td} ${T.num} ${T.muted}`}>{e.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </Card>

            <Card testid="mon-by-user" title="Heaviest customers" hint="API calls, last 7 days">
              {d.by_user.length === 0 ? (
                <Empty label="No per-customer data" />
              ) : (
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className={T.th}>Customer</th>
                      <th className={T.th}>Calls</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.by_user.map((u) => (
                      <tr key={u.user_id} className={T.row}>
                        <td className={`${T.td} text-[13px]`}>{u.email}</td>
                        <td className={`${T.td} ${T.num}`}>{u.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>

            <Card testid="mon-by-ip" title="Top IPs" hint="SHA-256 hashed, 30-day retention">
              {d.by_ip.length === 0 ? (
                <Empty label="No IP data" />
              ) : (
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className={T.th}>IP hash</th>
                      <th className={T.th}>Calls</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.by_ip.map((i) => (
                      <tr key={i.ip_hash} className={T.row}>
                        <td className={`${T.td} ${T.num} text-xs ${T.muted}`}>{i.ip_hash}…</td>
                        <td className={`${T.td} ${T.num}`}>{i.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          </div>
        </div>
      )}
    </OpsShell>
  );
}
